import { ReactNode, useCallback, useEffect, useId, useRef } from 'react';
import CloseButton from './CloseButton';
import { useModalContext } from '../../contexts/ModalContext';
import { TEXT } from './Typography';

/**
 * The scrim, for the ~100 screens that build their own modal rather than using
 * this component. Migrating those structurally means inferring each one's open
 * condition and close handler, which is a per-screen job — but there is no
 * reason for them to disagree about how a modal LOOKS, and they did: bg-black/40
 * next to bg-opacity-50 next to bg-gray-900/60, some blurred, some not, some
 * clipping tall content while this one scrolls it.
 *
 * Deliberately carries no `z-` class. Stacking is the one part that genuinely
 * differs — a modal opened from inside another has to sit above it — so the
 * call site states it: `className={`z-50 ${MODAL_OVERLAY_CLASS}`}`. Putting a
 * default here would mean two z- utilities on one element and Tailwind picking
 * the winner by stylesheet order.
 */
export const MODAL_OVERLAY_CLASS =
  'fixed inset-0 flex items-start justify-center px-3 py-4 sm:p-4 md:p-6 bg-black/50 overflow-y-auto';

/** The scrim's safe-area padding, so a modal clears a notch on iOS. */
export const MODAL_OVERLAY_STYLE = {
  paddingLeft: 'max(0.75rem, env(safe-area-inset-left))',
  paddingRight: 'max(0.75rem, env(safe-area-inset-right))',
  paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
} as const;

/** The panel's own surface. Width, height and scrolling stay with the caller. */
export const MODAL_PANEL_CLASS = 'bg-white rounded-lg shadow-xl';

/** Open dialogs, oldest first: the last one is on top. */
const OPEN_DIALOGS: object[] = [];

/** What counts as focusable for the focus trap and the initial focus. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Text, or text with an icon. Names the dialog for screen readers. */
  title?: ReactNode;
  /** A line under the title: what this dialog is about. */
  description?: ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  showCloseButton?: boolean;
  closeOnBackdrop?: boolean;
  /**
   * Close the modal when the user presses Escape. Defaults to true to
   * preserve existing behaviour everywhere. Set to false for modals where
   * a stray Escape press during typing would lose work (e.g. the
   * timesheet entry form), so closure has to be an explicit user action
   * via the X button or an in-modal action button.
   */
  closeOnEscape?: boolean;
  className?: string;
  /** Content to render in the header, positioned between title and close button */
  headerActions?: ReactNode;
  /**
   * False while the dialog must not be dismissed (e.g. mid-publish): the close
   * button is disabled and Escape and the backdrop do nothing.
   */
  dismissible?: boolean;
  /**
   * The overlay's stacking class. A dialog opened over something already at
   * z-50 (the planner's full-screen mode) has to sit above it.
   */
  zIndexClass?: string;
}

/**
 * What makes an overlay a dialog: it hides the mobile menu while open, locks
 * page scroll, moves focus in and back to the opener, keeps Tab inside, and
 * closes on an Escape nothing inside already handled. Shared by Modal and
 * Drawer so the two cannot drift.
 */
export function useDialog({
  isOpen,
  onClose,
  closeOnEscape = true,
  dismissible = true,
}: {
  isOpen: boolean;
  onClose: () => void;
  closeOnEscape?: boolean;
  dismissible?: boolean;
}) {
  const { registerModal, unregisterModal } = useModalContext();
  const panelRef = useRef<HTMLDivElement>(null);
  // Whatever had focus when the modal opened, so it can be given back.
  const openerRef = useRef<Element | null>(null);
  const tokenRef = useRef({});

  // Register/unregister with modal context to hide hamburger menu
  useEffect(() => {
    if (isOpen) {
      registerModal();
      return () => unregisterModal();
    }
  }, [isOpen, registerModal, unregisterModal]);

  // Page scroll stays locked until the LAST open dialog closes: a confirm
  // closing over an editor must not unlock the page behind both.
  useEffect(() => {
    if (!isOpen) return;
    const token = tokenRef.current;
    OPEN_DIALOGS.push(token);
    document.body.style.overflow = 'hidden';
    return () => {
      OPEN_DIALOGS.splice(OPEN_DIALOGS.indexOf(token), 1);
      if (OPEN_DIALOGS.length === 0) document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Focus moves into the dialog on open and back to the trigger on close.
  // Without the restore, dismissing a modal drops the caret at the top of the
  // document and a keyboard user has to tab back to where they were.
  useEffect(() => {
    if (!isOpen) return;
    openerRef.current = document.activeElement;
    // A field inside that asked for focus (autoFocus) keeps it; otherwise the
    // first control -- usually the close button -- would take it from them.
    if (!panelRef.current?.contains(document.activeElement)) {
      const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? panelRef.current)?.focus();
    }
    return () => {
      const opener = openerRef.current;
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
  }, [isOpen]);

  // Tab cycles within the dialog rather than escaping to the page behind it.
  const trapFocus = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
      .filter(el => el.offsetParent !== null || el === document.activeElement);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  useEffect(() => {
    if (!closeOnEscape || !dismissible) return;
    const handleEscape = (e: KeyboardEvent) => {
      // An Escape a control inside already handled (closing its own
      // dropdown) is not a request to close the dialog.
      // Only the top dialog answers, so Escape on a confirm closes the
      // confirm and not the editor underneath it.
      if (e.key === 'Escape' && isOpen && !e.defaultPrevented && OPEN_DIALOGS[OPEN_DIALOGS.length - 1] === tokenRef.current) {
        onClose();
        // Claimed, so a dialog below that hears it next skips it -- even if
        // this one's close has already flushed and it no longer reads as top.
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape, dismissible]);

  return { panelRef, trapFocus };
}

export default function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnBackdrop = false,
  closeOnEscape = true,
  className = '',
  headerActions,
  dismissible = true,
  zIndexClass = 'z-50',
}: ModalProps) {
  const titleId = useId();
  const { panelRef, trapFocus } = useDialog({ isOpen, onClose, closeOnEscape, dismissible });

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
    full: 'max-w-7xl',
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdrop && dismissible && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className={`${zIndexClass} ${MODAL_OVERLAY_CLASS}`}
      style={MODAL_OVERLAY_STYLE}
      onClick={handleBackdropClick}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : 'Dialog'}
        tabIndex={-1}
        onKeyDown={trapFocus}
        className={`${MODAL_PANEL_CLASS} w-full ${sizeClasses[size]} my-4 sm:my-8 relative focus:outline-none ${className}`}
      >
        {/* Close Button - Always top right */}
        {showCloseButton && (
          <CloseButton
            onClick={onClose}
            position="absolute"
            label="Close modal"
            disabled={!dismissible}
          />
        )}

        {/* Header with title and optional actions */}
        {title && (
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <h2 id={titleId} className={`sm:text-xl truncate ${TEXT.sectionTitle}`}>
                {title}
              </h2>
              {description && <p className={`mt-1 ${TEXT.bodyMuted}`}>{description}</p>}
            </div>
            {headerActions && <div className="flex-shrink-0 mr-9 sm:mr-8">{headerActions}</div>}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

interface ModalBodyProps {
  children: ReactNode;
  className?: string;
}

export function ModalBody({ children, className = '' }: ModalBodyProps) {
  return <div className={`px-4 sm:px-6 py-3 sm:py-4 ${className}`}>{children}</div>;
}

interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

export function ModalFooter({ children, className = '' }: ModalFooterProps) {
  return (
    <div
      // rounded-b-lg, matching the panel. A footer radius different from the panel's
      // leaves a visible step at both bottom corners on every modal.
      className={`flex items-center justify-end gap-2 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg ${className}`}
    >
      {children}
    </div>
  );
}
